import { CommissionType } from '../../../@types';
import { CommissionPicks } from '../../../@types/api';
import { COMMISSION_TYPES } from '../../../constants/commission';

export type AgentCommissionResponse = {
  type: CommissionType;
  percentage: number;
  picks: CommissionPicks<number>;
};

export type AffiliateCommissionResponse = {
  type: typeof COMMISSION_TYPES.CPA;
  cpa?: {
    amount: number;
    holdPeriod: number;
    minSportsbookWagering?: number | null;
    minCasinoWagering?: number | null;
    newPlayer?: boolean;
    kyc?: boolean;
  };
};

export type AffiliateCommissionFormData = {
  affiliateCommissionType: typeof COMMISSION_TYPES.CPA;
  cpaAmount: string;
  holdPeriod: string;
  minSportsbookWagering: string;
  minCasinoWagering: string;
  newPlayer: boolean;
  kyc: boolean;
};

export type AffiliateQualificationField = 'newPlayer' | 'kyc';

const toFormValue = (value: number | undefined) => value?.toString() ?? '0';
const toOptionalFormValue = (value: number | null | undefined) => value?.toString() ?? '';
const mapOptionalWagering = (value: string, currentValue?: number | null): number | null | undefined => {
  if (value !== '') return Number(value);
  if (currentValue != null) return null;

  return undefined;
};

const mapQualificationCriteria = (
  data: AffiliateCommissionFormData,
  qualificationFields: AffiliateQualificationField[]
) =>
  qualificationFields.reduce<Partial<Pick<AffiliateCommissionFormData, AffiliateQualificationField>>>(
    (criteria, field) => ({ ...criteria, [field]: data[field] }),
    {}
  );

export const mapAffiliateCommissionToForm = (
  commission?: AffiliateCommissionResponse
): AffiliateCommissionFormData => ({
  affiliateCommissionType: COMMISSION_TYPES.CPA,
  cpaAmount: toFormValue(commission?.cpa?.amount),
  holdPeriod: toFormValue(commission?.cpa?.holdPeriod),
  minSportsbookWagering: toOptionalFormValue(commission?.cpa?.minSportsbookWagering),
  minCasinoWagering: toOptionalFormValue(commission?.cpa?.minCasinoWagering),
  newPlayer: commission?.cpa?.newPlayer ?? false,
  kyc: commission?.cpa?.kyc ?? false,
});

export const mapAffiliateCommissionPatch = (
  data: AffiliateCommissionFormData,
  currentCpa?: AffiliateCommissionResponse['cpa'],
  qualificationFields: AffiliateQualificationField[] = ['newPlayer', 'kyc']
) => {
  const minSportsbookWagering = mapOptionalWagering(data.minSportsbookWagering, currentCpa?.minSportsbookWagering);
  const minCasinoWagering = mapOptionalWagering(data.minCasinoWagering, currentCpa?.minCasinoWagering);

  return {
    type: data.affiliateCommissionType,
    cpa: {
      amount: Number(data.cpaAmount),
      holdPeriod: Number(data.holdPeriod),
      ...(minSportsbookWagering !== undefined && { minSportsbookWagering }),
      ...(minCasinoWagering !== undefined && { minCasinoWagering }),
      ...mapQualificationCriteria(data, qualificationFields),
    },
  };
};
